import React, { useCallback, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { Typography, Button, theme, Tooltip } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";

import { convertTimestampToDate, reduceText } from "../../../../utils/Formaters";

const { Text } = Typography;

import { formatCurrency } from "../../utils";

import PropTypes from "prop-types";

const StyledListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #ddd;
  background-color: ${({ isSelected, colorSelected }) => (isSelected ? colorSelected : "")};
  cursor: pointer;
  transition: background 0.2s ease-in-out;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;

  &:hover {
    background-color: ${({ colorHover }) => (colorHover)};
  }
`;

const RowLeftContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const RowRightContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  white-space: nowrap;
`;

const RowAmount = styled(Text)`
  font-weight: bold;
  color: ${({ isPositive }) => (isPositive ? "#008000" : "#D80000")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowButton = styled(Button)`
  border: none;
  background: none;
  color: #007bff;
  flex-shrink: 0;

  &:hover {
    color: #0056b3;
  }
`;
	
const RowDescription = styled.div`
	font-size: 12px;	
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const RowHeader = styled.div`
	font-weight: bold;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const Row = ({ index, style, data }) => {
	const {
		transactions,
		selectedTransaction,
		onSelectedTransaction,
		setTransactionToView,
		currency,
		currencyGetter,
		showEntityName,
		textLength,
	} = data;

	const item = transactions[index];
	if (!item) return null;

	const { token : antToken } = theme.useToken();

	const isSelected = item.id === selectedTransaction?.id;
	const isPositive = item?.position === "in";
	const formattedDate = dayjs(convertTimestampToDate(item.date)).format("DD-MM-YYYY");
	const entityName = showEntityName(item?.entity_id, item?.position);

	return (
		<div style={{ ...style, padding: "5px", width: "100%" }}>
			<StyledListItem 
				isSelected={isSelected}
				colorSelected={antToken.colorSelectedRowTable}
				colorHover={antToken.controlItemBgHover}
				onClick={() => onSelectedTransaction(isSelected ? {} : item)}
			>
				<RowLeftContent>
					<Tooltip placement="topLeft" title={`${formattedDate} - ${entityName}`}>
						<RowHeader>{`${formattedDate} - ${entityName}`}</RowHeader>
					</Tooltip>
					<RowDescription>{reduceText(item.description, textLength)}</RowDescription>
				</RowLeftContent>

				<RowRightContent>
					<RowAmount isPositive={isPositive}>
						{`${currencyGetter[currency]} ${isPositive ? "" : "-"}${formatCurrency(item?.amount)}`}
					</RowAmount>
					<RowButton
						type="text"
						icon={<UnorderedListOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							setTransactionToView(item);
						}}
					/>
				</RowRightContent>

			</StyledListItem>
		</div>
	);
};

Row.propTypes = {
	index: PropTypes.number.isRequired,
	style: PropTypes.object.isRequired,
	data: PropTypes.shape({
		transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
		selectedTransaction: PropTypes.object,
		onSelectedTransaction: PropTypes.func.isRequired,
		setTransactionToView: PropTypes.func.isRequired,
		currency: PropTypes.string.isRequired,
		currencyGetter: PropTypes.object.isRequired,
		showEntityName: PropTypes.func.isRequired,
		textLength: PropTypes.number.isRequired,		
	}).isRequired,
};

export const VirtualizedList = ({ 
	transactions = [], 
	selectedTransaction, 
	onSelectedTransaction, 
	setTransactionToView, 
	currency, 
	currencyGetter,
	showEntityName,
	textLength,
	tableHeight,
}) => {
	const itemData = useMemo(() => ({
		transactions,
		selectedTransaction,
		onSelectedTransaction,
		setTransactionToView,
		currency,
		currencyGetter,
		showEntityName,
		textLength,
	}), [
		transactions,
		selectedTransaction,
		onSelectedTransaction,
		setTransactionToView,
		currency,
		currencyGetter,
		showEntityName,
		textLength
	]);
	
	const RowRenderer = useCallback((props) => <Row {...props} />, []);
	
	return (
		<List
			height={tableHeight}
			itemCount={transactions.length}
			itemSize={60}
			width="100%"
			itemData={itemData}
			style={{ overflowX: "hidden" }}
		>
			{RowRenderer}
		</List>
	);
};

VirtualizedList.propTypes = {
	transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
	selectedTransaction: PropTypes.object,
	onSelectedTransaction: PropTypes.func.isRequired,
	setTransactionToView: PropTypes.func.isRequired,
	currency: PropTypes.string.isRequired,
	currencyGetter: PropTypes.object.isRequired,
	showEntityName: PropTypes.func.isRequired,
	textLength: PropTypes.number.isRequired,
	tableHeight: PropTypes.number.isRequired,
};
